import { Observable, combineLatest, map, OperatorFunction } from 'rxjs';

function isSelectedItemOutsideOfContainer(
  itemWidth: number,
  containerWidth: number,
  selectedItemIndex: number,
): boolean {
  const selectedItemEndCoordinate = (selectedItemIndex + 1) * itemWidth;
  return selectedItemEndCoordinate >= containerWidth;
}

function moveSelectedItemToFirstPosition<T>(
  items: T[],
  selectedItemIndex: number,
): T[] {
  const itemsCopy = items.slice();
  const newFirst = itemsCopy.splice(selectedItemIndex, 1)[0];
  if (!newFirst) {
    return [...itemsCopy];
  }
  return [newFirst, ...itemsCopy];
}

/**
 * Moves the selected item to the first array position if it would go outside of its container due to its overflow.
 *
 * ```
 *   +-----------+
 *   | === === ==|= <-- this item will be moved to the first position
 *   +-----------+
 * ```
 *
 * @param itemWidth width of each item including its margin.
 * @param containerWidth$ {@link Observable} of the visible container width (think of `clientWidth`, not `scrollWidth`).
 * @param selectedItemIndex$ {@link Observable} of the currently selected item index.
 * @returns `Observable` of the possibly reordered array.
 */
export function reorderOnOverflow<T>(
  itemWidth: number,
  containerWidth$: Observable<number>,
  selectedItemIndex$: Observable<number>,
): OperatorFunction<T[], T[]> {
  function operator(unreorderedItems$: Observable<T[]>): Observable<T[]> {
    return combineLatest([
      unreorderedItems$,
      containerWidth$,
      selectedItemIndex$,
    ]).pipe(
      map(([unreorderedItems, containerWidth, selectedItemIndex]) =>
        isSelectedItemOutsideOfContainer(
          itemWidth,
          containerWidth,
          selectedItemIndex,
        )
          ? moveSelectedItemToFirstPosition<T>(
              unreorderedItems,
              selectedItemIndex,
            )
          : unreorderedItems,
      ),
    );
  }

  return operator;
}
